import type { ReactNode } from 'react';
import { Bird, Cat, Dog, Rabbit } from 'lucide-react';
import { FarmAnimalIcon } from '../../ui';

import type { TransportRequestDto } from '../../features/carrierCabinet/api';
import { readStoredLanguage } from '../../i18n/locale';
import { getRuntimeTimezone } from '../../i18n/preferences';

const PET_LABELS: Record<string, string> = {
  dog: 'Собака',
  cat: 'Кошка',
  rodent: 'Грызун',
  bird: 'Птица',
  farm: 'Сельхоз',
  other: 'Другое',
};

const CARRIER_BOX_LABELS: Record<string, string> = {
  own_box: 'Своя переноска',
  carrier_box: 'Нужна переноска перевозчика',
};

const DOCUMENTS_STATUS_LABELS: Record<string, string> = {
  ready: 'Документы готовы',
  in_progress: 'Документы оформляются',
};

export function hashNumber(name: string): number | null {
  const query = window.location.hash.split('?')[1] ?? '';
  const value = Number(new URLSearchParams(query).get(name));
  return Number.isInteger(value) && value > 0 ? value : null;
}

export function money(value: number | null | undefined, currency = 'RUB') {
  if (value == null) return '';
  return new Intl.NumberFormat(readStoredLanguage() === 'en' ? 'en-GB' : 'ru-RU', {
    style: 'currency',
    currency,
    maximumFractionDigits: 0,
  }).format(value / 100);
}

export function dateLabel(value: string | null | undefined, fallbackYear?: number) {
  if (!value) return '';
  const date = new Date(`${value}T00:00:00`);
  if (isNaN(date.getTime())) return '';
  const currentYear = fallbackYear ?? new Date().getFullYear();
  const isSameYear = date.getFullYear() === currentYear;

  return new Intl.DateTimeFormat('ru-RU', {
    day: 'numeric',
    month: 'long',
    ...(isSameYear ? {} : { year: 'numeric' }),
  }).format(date);
}

export function dateRange(request: TransportRequestDto) {
  const from = dateLabel(request.pickup_not_before);
  const to = dateLabel(request.delivery_not_after);
  if (from && to) return `${from} – ${to}`;
  return from || to;
}

export function petLabel(request: TransportRequestDto) {
  const pet = request.pets[0];
  if (!pet) return '';
  return pet.breed || pet.custom_type || PET_LABELS[pet.pet_type] || '';
}

export function petDetails(request: TransportRequestDto) {
  const pet = request.pets[0];
  if (!pet) return '';
  return [petLabel(request), pet.weight_kg ? `${Number(pet.weight_kg)} кг` : '', pet.count > 1 ? `${pet.count} питомца` : '']
    .filter(Boolean)
    .join(' · ');
}

export function budgetLabel(request: TransportRequestDto) {
  const low = money(request.budget_min_minor, request.currency);
  const high = money(request.budget_max_minor, request.currency);
  return low && high ? `${low} – ${high}` : low || high;
}

export function carrierBoxLabel(value: string) {
  if (!value) return '';
  return CARRIER_BOX_LABELS[value] ?? 'Не указано';
}

export function documentsStatusLabel(value: string) {
  if (!value) return '';
  return DOCUMENTS_STATUS_LABELS[value] ?? 'Не указано';
}

export function relativeTime(value: string | null) {
  if (!value) return '';
  const minutes = Math.max(1, Math.floor((Date.now() - new Date(value).getTime()) / 60000));
  if (minutes < 60) return `${minutes} мин назад`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} ч назад`;
  return `${Math.floor(hours / 24)} дн назад`;
}

export function dateTimeLabel(value: string | null | undefined) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return new Intl.DateTimeFormat(readStoredLanguage() === 'en' ? 'en-GB' : 'ru-RU', {
    day: 'numeric',
    month: 'long',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: getRuntimeTimezone(),
  }).format(date);
}

import { CabinetHeading, type CabinetHeadingProps } from '../../ui';

export const PageHeading = CabinetHeading;
export type PageHeadingProps = CabinetHeadingProps;

export function PetIcon({ type }: { type: string }) {
  const props = { 'aria-hidden': true, size: 24, strokeWidth: 1.5 } as const;
  if (type === 'cat') return <Cat {...props} />;
  if (type === 'rodent') return <Rabbit {...props} />;
  if (type === 'bird') return <Bird {...props} />;
  if (type === 'farm') return <FarmAnimalIcon {...props} />;
  return <Dog {...props} />;
}
